/**
 * Morse „Augen zu“ — die kurze Stimme. (13.10.2026)
 *
 * Nach einem falschen Buchstaben sagt eine Stimme „Das war M.“ — über den vorhandenen TTS-Weg der App (`/api/tts/prepare` +
 * `/api/tts/stream`, derselbe wie im Onboarding und beim Vorlesen), mit der gewählten Stimme. Kein eigener Sprach-Motor, nichts
 * wird gespeichert.
 *
 * Die Stimme ist Beiwerk: scheitert der Abruf (Netz, Dienst, Guthaben) oder blockiert das Gerät die Wiedergabe, löst `fertig`
 * trotzdem auf — der tiefe Ton und der langsame Buchstabe tragen die Rückmeldung allein. Eine Obergrenze (`STIMME_FRIST_MS`)
 * hält die Übung davor, an einem hängenden Abruf zu stehen.
 *
 * ⚠️ Nur am iPhone zu prüfen: dass das Abspielen (HTMLAudioElement) die Tonsitzung wieder freigibt, bevor das Mikrofon der nächsten
 *    Aufgabe kommt (der Anschluss ruft dort `sitzungZurueck()`), und dass es ohne Fingertipp starten darf.
 */
import { loadSettings } from "./settings.js";
import type { StimmeLauf } from "./morse-teile.js";

/** So lange (ms) darf Abruf plus Sprechen höchstens dauern. */
const STIMME_FRIST_MS = 7000;
/** Mehr als das spricht die Rückmeldung nie (der Server nimmt ≤ 300 Zeichen). */
const STIMME_MAX_ZEICHEN = 300;

export function stimmeAusApp(): (text: string) => StimmeLauf {
  return (text) => {
    let audio: HTMLAudioElement | null = null;
    let aus = false;
    let frist: ReturnType<typeof setTimeout> | undefined;
    const ende = (): void => {
      aus = true;
      if (frist) clearTimeout(frist);
      try {
        audio?.pause();
      } catch {
        /* schon zu */
      }
    };
    const sprechen = async (): Promise<void> => {
      try {
        const r = await fetch("/api/tts/prepare", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify({ text: text.slice(0, STIMME_MAX_ZEICHEN), voiceId: loadSettings().voiceId }),
        });
        const j = (await r.json().catch(() => null)) as { ok?: boolean; token?: string } | null;
        if (aus || !r.ok || !j?.ok || !j.token) return;
        const a = new Audio(`/api/tts/stream?token=${encodeURIComponent(j.token)}`);
        audio = a;
        await new Promise<void>((fertig) => {
          a.addEventListener("ended", () => fertig(), { once: true });
          a.addEventListener("error", () => fertig(), { once: true });
          a.addEventListener("pause", () => fertig(), { once: true });
          void a.play().catch(() => fertig());
        });
      } catch {
        /* Beiwerk */
      }
    };
    const fertig = Promise.race([
      sprechen(),
      new Promise<void>((auf) => {
        frist = setTimeout(() => {
          ende();
          auf();
        }, STIMME_FRIST_MS);
      }),
    ]).finally(() => {
      if (frist) clearTimeout(frist);
    });
    return { fertig, stopp: ende };
  };
}
